import { useState, useCallback } from "react";
import { getFriendlyErrorMessage } from "../utils/errorMapper";

const API_BASE = import.meta.env.VITE_WPP_API_BASE as string;

const STORAGE_TOKEN_KEY = "wpp_session_state";

function getTokenFromStorage(sessionName: string): string | null {
  try {
    const raw = localStorage.getItem(`${STORAGE_TOKEN_KEY}_${sessionName}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.wppToken || null;
  } catch {
    return null;
  }
}

export interface WppCatalogProduct {
  id: string;
  name: string;
  description?: string;
  price?: string;
  currency?: string;
  url?: string;
  retailerId?: string;
  image?: string; // base64 or url
  images?: string[]; // extra images
  reviewStatus?: string;
  availability?: string;
  isHidden?: boolean;
}

export interface WppCatalogCollection {
  id: string;
  name: string;
  products?: string[];
}

export interface AddProductInput {
  name: string;
  image: string; // base64
  description?: string;
  price?: string;
  url?: string;
  retailerId?: string;
  currency?: string;
}

export interface EditProductInput {
  name?: string;
  description?: string;
  price?: string;
  url?: string;
  retailerId?: string;
  currency?: string;
}

export function useWhatsAppCatalog(sessionName: string, phone: string) {
  const [products, setProducts] = useState<WppCatalogProduct[]>([]);
  const [collections, setCollections] = useState<WppCatalogCollection[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setErrorState] = useState<string | null>(null);
  const setError = useCallback((err: string | null) => {
    setErrorState(err ? getFriendlyErrorMessage(err) : null);
  }, []);
  const [cartEnabled, setCartEnabledState] = useState<boolean | null>(null);

  const apiHeaders = useCallback((): Record<string, string> => {
    const token = getTokenFromStorage(sessionName) || "";
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }, [sessionName]);

  // #1 List Products
  const listProducts = useCallback(
    async (qnt = 50): Promise<WppCatalogProduct[]> => {
      if (!sessionName || !phone) return [];
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${API_BASE}/${sessionName}/get-products?phone=${encodeURIComponent(phone)}&qnt=${qnt}`,
          { headers: apiHeaders() },
        );
        if (!res.ok) throw new Error(`Failed to load products (${res.status})`);
        const json = await res.json();
        const list: WppCatalogProduct[] = Array.isArray(json) ? json : json.response || json.data || json.products || [];
        setProducts(list);
        return list;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to load products";
        setError(msg);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [sessionName, phone, apiHeaders],
  );

  // #2 Get Single Product
  const getProductById = useCallback(
    async (id: string): Promise<WppCatalogProduct | null> => {
      if (!sessionName || !phone || !id) return null;
      try {
        const res = await fetch(
          `${API_BASE}/${sessionName}/get-product-by-id?phone=${encodeURIComponent(phone)}&id=${encodeURIComponent(id)}`,
          { headers: apiHeaders() },
        );
        if (!res.ok) throw new Error(`Failed to get product (${res.status})`);
        const json = await res.json();
        const product: WppCatalogProduct | null =
          json.response || json.data || json.product || (typeof json === "object" && json.id ? json : null);
        return product;
      } catch (err) {
        console.error("getProductById error:", err);
        return null;
      }
    },
    [sessionName, phone, apiHeaders],
  );

  // #3 Add Product
  const addProduct = useCallback(
    async (input: AddProductInput): Promise<{ id?: string; error?: string } | null> => {
      if (!sessionName) return { error: "No active session" };
      setActionLoading("add");
      setError(null);
      try {
        // Strip empty optional fields – WhatsApp API may reject empty strings
        const body: Record<string, string> = { name: input.name, image: input.image };
        if (input.description?.trim()) body.description = input.description;
        if (input.price?.trim()) body.price = input.price;
        if (input.currency?.trim()) body.currency = input.currency;
        if (input.url?.trim()) body.url = input.url;
        if (input.retailerId?.trim()) body.retailerId = input.retailerId;

        const res = await fetch(`${API_BASE}/${sessionName}/add-product`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          let detail = "";
          try {
            const errJson = await res.json();
            detail = errJson.message || errJson.error || JSON.stringify(errJson);
          } catch {
            detail = `HTTP ${res.status}`;
          }
          throw new Error(detail || `HTTP ${res.status}`);
        }
        const json = await res.json();
        return json;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to add product";
        setError(msg);
        return { error: msg };
      } finally {
        setActionLoading(null);
      }
    },
    [sessionName, apiHeaders],
  );

  // #4 Edit Product
  const editProduct = useCallback(
    async (id: string, options: EditProductInput): Promise<boolean> => {
      if (!sessionName || !id) return false;
      setActionLoading("edit");
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/${sessionName}/edit-product`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ id, options }),
        });
        if (!res.ok) throw new Error(`Failed to edit product (${res.status})`);
        await res.json();
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to edit product";
        setError(msg);
        return false;
      } finally {
        setActionLoading(null);
      }
    },
    [sessionName, apiHeaders],
  );

  // #5 Change Main Product Image
  const changeProductImage = useCallback(
    async (id: string, base64: string): Promise<boolean> => {
      if (!sessionName || !id || !base64) return false;
      setActionLoading("image");
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/${sessionName}/change-product-image`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ id, base64 }),
        });
        if (!res.ok) throw new Error(`Failed to change image (${res.status})`);
        await res.json();
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to change image";
        setError(msg);
        return false;
      } finally {
        setActionLoading(null);
      }
    },
    [sessionName, apiHeaders],
  );

  // #6 Add Extra Product Image
  const addProductImage = useCallback(
    async (id: string, base64: string): Promise<boolean> => {
      if (!sessionName || !id || !base64) return false;
      setActionLoading("addImage");
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/${sessionName}/add-product-image`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ id, base64 }),
        });
        if (!res.ok) throw new Error(`Failed to add image (${res.status})`);
        await res.json();
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to add image";
        setError(msg);
        return false;
      } finally {
        setActionLoading(null);
      }
    },
    [sessionName, apiHeaders],
  );

  // #7 Remove Product Image by Index
  const removeProductImage = useCallback(
    async (id: string, index: number): Promise<boolean> => {
      if (!sessionName || !id) return false;
      setActionLoading("removeImage");
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/${sessionName}/remove-product-image`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ id, index }),
        });
        if (!res.ok) throw new Error(`Failed to remove image (${res.status})`);
        await res.json();
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to remove image";
        setError(msg);
        return false;
      } finally {
        setActionLoading(null);
      }
    },
    [sessionName, apiHeaders],
  );

  // #8 Delete Product
  const deleteProduct = useCallback(
    async (id: string): Promise<boolean> => {
      if (!sessionName || !id) return false;
      setActionLoading("delete");
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/${sessionName}/del-products`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ id }),
        });
        if (!res.ok) throw new Error(`Failed to delete product (${res.status})`);
        await res.json();
        setProducts((prev) => prev.filter((p) => p.id !== id));
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to delete product";
        setError(msg);
        return false;
      } finally {
        setActionLoading(null);
      }
    },
    [sessionName, apiHeaders],
  );

  // #9 Set Product Visibility
  const setProductVisibility = useCallback(
    async (id: string, value: boolean): Promise<boolean> => {
      if (!sessionName || !id) return false;
      setActionLoading("visibility");
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/${sessionName}/set-product-visibility`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ id, value }),
        });
        if (!res.ok) throw new Error(`Failed to set visibility (${res.status})`);
        await res.json();
        setProducts((prev) =>
          prev.map((p) => (p.id === id ? { ...p, isHidden: !value } : p)),
        );
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to set visibility";
        setError(msg);
        return false;
      } finally {
        setActionLoading(null);
      }
    },
    [sessionName, apiHeaders],
  );

  // #10 Create Collection
  const createCollection = useCallback(
    async (name: string, productIds: string[]): Promise<{ id?: string } | null> => {
      if (!sessionName) return null;
      setActionLoading("createCollection");
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/${sessionName}/create-collection`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ name, products: productIds }),
        });
        if (!res.ok) throw new Error(`Failed to create collection (${res.status})`);
        const json = await res.json();
        return json;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to create collection";
        setError(msg);
        return null;
      } finally {
        setActionLoading(null);
      }
    },
    [sessionName, apiHeaders],
  );

  // #11 Edit Collection
  const editCollection = useCallback(
    async (id: string, options: { name?: string; products?: string[] }): Promise<boolean> => {
      if (!sessionName || !id) return false;
      setActionLoading("editCollection");
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/${sessionName}/edit-collection`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ id, options }),
        });
        if (!res.ok) throw new Error(`Failed to edit collection (${res.status})`);
        await res.json();
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to edit collection";
        setError(msg);
        return false;
      } finally {
        setActionLoading(null);
      }
    },
    [sessionName, apiHeaders],
  );

  // #12 Delete Collection
  const deleteCollection = useCallback(
    async (id: string): Promise<boolean> => {
      if (!sessionName || !id) return false;
      setActionLoading("deleteCollection");
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/${sessionName}/del-collection`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ id }),
        });
        if (!res.ok) throw new Error(`Failed to delete collection (${res.status})`);
        await res.json();
        setCollections((prev) => prev.filter((c) => c.id !== id));
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to delete collection";
        setError(msg);
        return false;
      } finally {
        setActionLoading(null);
      }
    },
    [sessionName, apiHeaders],
  );

  // #13 List Collections
  const listCollections = useCallback(
    async (qnt = 20, max = 20): Promise<WppCatalogCollection[]> => {
      if (!sessionName || !phone) return [];
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${API_BASE}/${sessionName}/get-collections?phone=${encodeURIComponent(phone)}&qnt=${qnt}&max=${max}`,
          { headers: apiHeaders() },
        );
        if (!res.ok) throw new Error(`Failed to load collections (${res.status})`);
        const json = await res.json();
        const list: WppCatalogCollection[] =
          Array.isArray(json) ? json : json.response || json.data || json.collections || [];
        setCollections(list);
        return list;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to load collections";
        setError(msg);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [sessionName, phone, apiHeaders],
  );

  // #14 Set Cart Enabled
  const setCartEnabled = useCallback(
    async (enabled: boolean): Promise<boolean> => {
      if (!sessionName) return false;
      setActionLoading("cart");
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/${sessionName}/set-cart-enabled`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ enabled }),
        });
        if (!res.ok) throw new Error(`Failed to set cart (${res.status})`);
        await res.json();
        setCartEnabledState(enabled);
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to set cart";
        setError(msg);
        return false;
      } finally {
        setActionLoading(null);
      }
    },
    [sessionName, apiHeaders],
  );

  // #15 Get Business Catalog
  const getBusinessCatalog = useCallback(
    async (targetPhone: string): Promise<unknown | null> => {
      if (!sessionName || !targetPhone) return null;
      try {
        const res = await fetch(
          `${API_BASE}/${sessionName}/get-business-profiles-products?phone=${encodeURIComponent(targetPhone)}`,
          { headers: apiHeaders() },
        );
        if (!res.ok) throw new Error(`Failed to get catalog (${res.status})`);
        return await res.json();
      } catch (err) {
        console.error("getBusinessCatalog error:", err);
        return null;
      }
    },
    [sessionName, apiHeaders],
  );

  // #16 Get Order By MessageId
  const getOrderByMessageId = useCallback(
    async (messageId: string): Promise<unknown | null> => {
      if (!sessionName || !messageId) return null;
      try {
        const res = await fetch(
          `${API_BASE}/${sessionName}/get-order-by-messageId/${encodeURIComponent(messageId)}`,
          { headers: apiHeaders() },
        );
        if (!res.ok) throw new Error(`Failed to get order (${res.status})`);
        return await res.json();
      } catch (err) {
        console.error("getOrderByMessageId error:", err);
        return null;
      }
    },
    [sessionName, apiHeaders],
  );

  // Utility: refresh all data
  const refreshAll = useCallback(async () => {
    await listProducts();
    await listCollections();
  }, [listProducts, listCollections]);

  return {
    products,
    collections,
    loading,
    actionLoading,
    error,
    cartEnabled,
    listProducts,
    getProductById,
    addProduct,
    editProduct,
    deleteProduct,
    setProductVisibility,
    changeProductImage,
    addProductImage,
    removeProductImage,
    createCollection,
    editCollection,
    deleteCollection,
    listCollections,
    setCartEnabled,
    getBusinessCatalog,
    getOrderByMessageId,
    refreshAll,
  };
}